/**
 * Zugang zum Backend.
 *
 * Der Zugangstoken wird kurzlebig ausgegeben und ueber `/auth/refresh`
 * erneuert. Wichtig dabei: **nur ein 401 meldet ab.** Ein Timeout, ein
 * Netzwerkabbruch oder ein 500er sind voruebergehend — wer dabei den Token
 * wegwirft, wirft den Nutzer mitten aus der Arbeit, obwohl seine Sitzung
 * gueltig ist.
 */

const TOKEN_KEY = 'autograb.token';
const REFRESH_KEY = 'autograb.refresh';

export class ApiError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiError';
  }
}

export const tokens = {
  get access() {
    return localStorage.getItem(TOKEN_KEY);
  },
  get refresh() {
    return localStorage.getItem(REFRESH_KEY);
  },
  set(access: string, refresh?: string) {
    localStorage.setItem(TOKEN_KEY, access);
    if (refresh) localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

let refreshing: Promise<boolean> | null = null;

async function tryRefresh(): Promise<boolean> {
  // Nur einmal gleichzeitig erneuern — sonst laufen bei einem Seitenwechsel
  // fuenf parallele Anfragen in fuenf Refresh-Versuche.
  if (refreshing) return refreshing;
  const rt = tokens.refresh;
  if (!rt) return false;
  refreshing = (async () => {
    try {
      const r = await fetch('/api/auth/refresh', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!r.ok) return false;
      const d = await r.json();
      if (!d?.access_token) return false;
      tokens.set(d.access_token, d.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

export async function api<T = unknown>(
  path: string,
  init: RequestInit & { retryAuth?: boolean } = {},
): Promise<T> {
  const { retryAuth = true, ...rest } = init;
  const headers = new Headers(rest.headers);
  headers.set('Accept', 'application/json');
  if (rest.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  const t = tokens.access;
  if (t) headers.set('Authorization', `Bearer ${t}`);

  const res = await fetch(`/api${path}`, { ...rest, headers });

  if (res.status === 401 && retryAuth && (await tryRefresh())) {
    return api<T>(path, { ...init, retryAuth: false });
  }
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const d = await res.json();
      const detail = d?.detail;
      if (typeof detail === 'string') msg = detail;
      else if (Array.isArray(detail) && detail[0]?.msg) msg = detail[0].msg;
    } catch {
      /* Antwort ohne JSON — die Statuszeile muss reichen */
    }
    throw new ApiError(res.status, msg);
  }
  if (res.status === 204) return undefined as T;
  return res.json() as Promise<T>;
}

// --- Datentypen, wie das Backend sie liefert ---------------------------------

export type CronJob = {
  id: number;
  user_id: number;
  name: string;
  search_query: string;
  search_type: string;
  category: string | null;
  cron_expression: string;
  is_enabled: boolean;
  auto_download: boolean;
  min_size_mb: number | null;
  max_size_mb: number | null;
  regex_pattern: string | null;
  preferred_resolution: string | null;
  target_season: number | null;
  extra_params: string | null;
  upgrade_propers: boolean;
  quality_cutoff: string | null;
  last_run_at: string | null;
  created_at: string;
  updated_at: string;
};

export type RunLog = {
  id: number;
  cron_job_id: number;
  started_at: string;
  finished_at: string | null;
  results_found: number;
  downloads_pushed: number;
  status: string;
  error_message: string | null;
  details: string | null;
};

export type Download = {
  id: number;
  title: string;
  guid: string;
  size_bytes: number | null;
  category: string | null;
  source: string | null;
  status: string;
  season: number | null;
  episode: number | null;
  quality_score: number | null;
  downloaded_at: string;
};

export type Stats = {
  total: number;
  total_bytes: number;
  by_status: Record<string, { count: number; bytes: number }>;
  by_source: Record<string, number>;
  failed: number;
  completed: number;
  in_flight: number;
};

export type NzbgetStatus = {
  FreeDiskSpaceMB?: number;
  DownloadRate?: number;
  RemainingSizeMB?: number;
  ServerStandBy?: boolean;
  [k: string]: unknown;
};
